import type { HomeWidget, WidgetArea } from "@ronin/contracts";
import { MAX_BOARD_WIDGETS } from "@ronin/contracts";
import { Button, cn, Tooltip } from "@ronin/ui";
import { getRouteApi } from "@tanstack/react-router";
import { Check, LayoutGrid, Loader2, type LucideIcon, Pencil, Plus } from "lucide-react";
import { useState } from "react";
import { BoardsDialog } from "../components/home/boards-dialog.js";
import { BoardsNavContext } from "../components/home/boards-nav.js";
import { FreeGrid, TileDnd } from "../components/home/free-grid.js";
import { useHome } from "../components/home/use-home.js";
import { WidgetDialog } from "../components/home/widget-dialog.js";
import { WidgetTile } from "../components/home/widget-tile.js";
import { NeedsConnection } from "../components/needs-connection.js";
import { needsConnection } from "../lib/errors.js";
import { useMediaQuery } from "../lib/use-media-query.js";

const route = getRouteApi("/app/");

type DialogState =
  | { mode: "closed" }
  | { mode: "add" }
  | { mode: "edit"; widget: HomeWidget }
  | { mode: "boards" };

/**
 * Home dashboard (spec12): boards as tabs, each a grid of tiles; the board is in the URL.
 * Editing starts from the floating button at the bottom right (FR8).
 */
export function HomePage() {
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const actions = useHome();
  const { data, isPending, isError } = actions.query;
  const offline = needsConnection(actions.query);
  const [editing, setEditing] = useState(false);
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });
  const close = () => setDialog({ mode: "closed" });

  const boards = data?.boards ?? [];
  // FR1: an unknown or missing board id shows the first board.
  const board = boards.find((b) => b.id === search.board) ?? boards[0];
  const widgets = board?.widgets ?? [];
  const top = data?.top ?? [];
  const nav = {
    boards,
    activeId: board?.id ?? "",
    select: (id: string) => void navigate({ search: { board: id } }),
  };
  // FR9: the free grid from lg (1024 px); below that the tiles flow in reading order.
  const free = useMediaQuery("(min-width: 64rem)");

  /** One grid: free from lg, reading order below (FR9). */
  const grid = (gridArea: WidgetArea, list: HomeWidget[], extraRows: number) =>
    free ? (
      <FreeGrid
        area={gridArea}
        widgets={list}
        editing={editing}
        extraRows={extraRows}
        onEdit={(widget) => setDialog({ mode: "edit", widget })}
        onDelete={(widget) => actions.removeWidget.mutate(widget)}
      />
    ) : (
      <div className="grid grid-flow-row-dense auto-rows-[0.5rem] grid-cols-4 gap-2 md:grid-cols-8">
        {list.map((widget) => (
          <WidgetTile
            key={widget.id}
            widget={widget}
            editing={editing}
            free={false}
            onEdit={() => setDialog({ mode: "edit", widget })}
            onResize={(dims) => actions.placeWidget(widget, dims)}
            onDelete={() => actions.removeWidget.mutate(widget)}
          />
        ))}
      </div>
    );

  /** In edit mode each grid is framed and named, so it is clear which one a tile is in. */
  const area = (
    gridArea: WidgetArea,
    label: string,
    hint: string,
    list: HomeWidget[],
    extraRows: number,
  ) =>
    editing ? (
      <section
        aria-label={label}
        className="flex flex-col gap-2 border border-dashed border-input p-2"
      >
        <p className="text-xs font-medium text-muted-foreground">
          {label} <span className="font-normal text-faint-foreground">· {hint}</span>
        </p>
        {grid(gridArea, list, extraRows)}
      </section>
    ) : (
      grid(gridArea, list, extraRows)
    );

  const content = (
    <>
      {/* FR9a: the top grid is the same on every board; the board grid below is per board.
          Tiles move between them by drag and drop; the top grid keeps one free row to drop into. */}
      {area("top", "Top", "on every board", top, 4)}
      {area("board", board?.name ?? "Board", "only on this board", widgets, 16)}
      {editing && !free && (
        <p className="text-xs text-faint-foreground">
          Tiles are arranged on a wider screen; here they show in reading order.
        </p>
      )}
    </>
  );

  return (
    // Room at the bottom so the floating buttons never cover the last tiles.
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 pb-16">
      <h1 className="sr-only">Home</h1>
      {offline && <NeedsConnection onRetry={() => void actions.query.refetch()} />}
      {isPending && !offline && (
        <p className="flex items-center gap-2 py-12 text-sm text-dim-foreground">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          Loading …
        </p>
      )}
      {isError && !offline && (
        <p className="border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Could not load your dashboard. Try reloading the page.
        </p>
      )}

      {board && (
        // FR4: no tab row; the Boards widget is the only board picker.
        <BoardsNavContext.Provider value={nav}>
          <TileDnd
            grids={{ top, board: widgets }}
            onPlace={(widget, patch) =>
              actions.placeWidget(widget, {
                ...patch,
                ...(patch.area === "board" && board && { boardId: board.id }),
              })
            }
            onDelete={(widget) => actions.removeWidget.mutate(widget)}
          >
            <div className="flex flex-col gap-4">{content}</div>
          </TileDnd>
        </BoardsNavContext.Provider>
      )}

      {board && (
        <div className="fixed right-[max(1rem,env(safe-area-inset-right))] bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-30 flex flex-col items-end gap-2 md:bottom-[calc(1rem+env(safe-area-inset-bottom))]">
          {editing && (
            <>
              <FloatingButton
                icon={LayoutGrid}
                label="Boards"
                onClick={() => setDialog({ mode: "boards" })}
              />
              {/* The + already says "add": the visible text is "Widget", the name "Add widget". */}
              <FloatingButton
                icon={Plus}
                label="Widget"
                name="Add widget"
                disabled={widgets.length >= MAX_BOARD_WIDGETS}
                onClick={() => setDialog({ mode: "add" })}
              />
            </>
          )}
          <FloatingButton
            icon={editing ? Check : Pencil}
            label={editing ? "Done" : "Edit dashboard"}
            pressed={editing}
            iconOnly={!editing}
            onClick={() => setEditing((e) => !e)}
          />
        </div>
      )}

      {board && (
        <WidgetDialog
          open={dialog.mode === "add" || dialog.mode === "edit"}
          widget={dialog.mode === "edit" ? dialog.widget : undefined}
          onOpenChange={(open) => !open && close()}
          onCreate={(input) =>
            actions.createWidget.mutateAsync({ boardId: board.id, input }).then(close)
          }
          onUpdate={(widget, patch) =>
            actions.updateWidget.mutateAsync({ widget, patch }).then(close)
          }
          onDelete={(widget) => {
            close();
            actions.removeWidget.mutate(widget);
          }}
        />
      )}
      <BoardsDialog
        open={dialog.mode === "boards"}
        onOpenChange={(open) => !open && close()}
        boards={boards}
        activeId={board?.id ?? ""}
        onOpenBoard={nav.select}
        actions={actions}
      />
    </div>
  );
}

/**
 * Plain and quiet (no accent): it should not stand out over the tiles. The labelled buttons
 * share one width, so the stack lines up.
 */
function FloatingButton({
  icon: Icon,
  label,
  name,
  onClick,
  pressed,
  disabled,
  iconOnly = false,
}: {
  icon: LucideIcon;
  label: string;
  /** Accessible name when it says more than the visible label. */
  name?: string;
  onClick: () => void;
  pressed?: boolean;
  disabled?: boolean;
  iconOnly?: boolean;
}) {
  const button = (
    <Button
      variant="ghost"
      aria-label={iconOnly ? label : name}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "border bg-card/80 text-muted-foreground backdrop-blur-lg hover:bg-muted hover:text-foreground",
        iconOnly ? "size-10 p-0" : "h-10 w-28 justify-start px-3.5",
      )}
    >
      <Icon />
      {!iconOnly && label}
    </Button>
  );
  return iconOnly ? (
    <Tooltip content={label} side="left">
      {button}
    </Tooltip>
  ) : (
    button
  );
}
